/**
 * Customer maintenance forms: edit, employments, documents, bank relationships and merge.
 * Messages are common-namespace keys; components never shape API payloads (toRequest mappers live here).
 */
import type { Schema } from '@dealdesk/api-schema';
import { z } from 'zod';

import { M, zDateOnly, zMoney, zOptionalEmiratesId, zRequiredString } from '@/business';

const NOT_FUTURE = 'validation.server.not_future';
const FORMAT = 'validation.server.format';

/** '' keeps the value on file (the client only ever sees the masked value). */
const keepOr = (re: RegExp, message: string) => z.union([z.literal(''), z.string().trim().regex(re, message)]);
const optionalDate = () => z.union([z.literal(''), zDateOnly()]);
const notAfter = (today: string) => (v: string) => !v || v <= today;

// ---------------------------------------------------------------- edit customer

export const customerEditSchema = z.object({
  name: zRequiredString(),
  nameAr: z.string(),
  // Empty when the date of birth is not shown to the user (no customer.pii.view): left out, so it stays on file.
  dateOfBirth: optionalDate(),
  nationality: zRequiredString(),
  // With or without separators; same structure + check-digit rule as the API (B1).
  emiratesId: zOptionalEmiratesId(),
  mobile: keepOr(/^\+9715\d{8}$/, M.mobile),
  email: z.union([z.literal(''), z.email(M.email)]),
  passportNumber: keepOr(/^[A-Za-z0-9]{6,12}$/, FORMAT),
  /** Why protected identity fields change; required (checked on submit) once the customer was submitted to a bank. */
  reason: z.string().max(500, 'validation.server.max_length'),
});
export type CustomerEditValues = z.infer<typeof customerEditSchema>;

/**
 * Protected identity fields (B1; the passport number since C2): after a bank submission they change only with
 * customer.identity.edit and a reason.
 */
export const IDENTITY_FIELDS = ['name', 'nameAr', 'emiratesId', 'dateOfBirth', 'nationality', 'passportNumber'] as const satisfies readonly Schema<'CustomerIdentityField'>[];

/**
 * The protected identity fields the form would change (a typed Emirates ID or passport number always counts: the
 * value on file is masked).
 */
export function changedIdentityFields(v: CustomerEditValues, c: Pick<Schema<'Customer'>, 'name' | 'nameAr' | 'dateOfBirth' | 'nationality'>): Schema<'CustomerIdentityField'>[] {
  const out: Schema<'CustomerIdentityField'>[] = [];
  if (v.name.trim() !== c.name) out.push('name');
  if ((v.nameAr.trim() || null) !== (c.nameAr ?? null)) out.push('nameAr');
  if (v.dateOfBirth && v.dateOfBirth !== c.dateOfBirth) out.push('dateOfBirth');
  if (v.nationality !== (c.nationality ?? '')) out.push('nationality');
  if (v.emiratesId) out.push('emiratesId');
  if (v.passportNumber) out.push('passportNumber');
  return out;
}

/** Identity fields come from the record; PII inputs start empty and are only sent when the user types a new value. */
export function toCustomerEditValues(c: Pick<Schema<'Customer'>, 'name' | 'nameAr' | 'dateOfBirth' | 'nationality'>): CustomerEditValues {
  return { name: c.name, nameAr: c.nameAr ?? '', dateOfBirth: c.dateOfBirth ?? '', nationality: c.nationality ?? '', emiratesId: '', mobile: '', email: '', passportNumber: '', reason: '' };
}

export function toCustomerUpdate(v: CustomerEditValues): Schema<'CustomerUpdate'> {
  return {
    name: v.name.trim(),
    nameAr: v.nameAr.trim() || null,
    ...(v.dateOfBirth ? { dateOfBirth: v.dateOfBirth } : {}),
    nationality: v.nationality,
    ...(v.emiratesId ? { emiratesId: v.emiratesId } : {}),
    ...(v.mobile ? { mobile: v.mobile } : {}),
    ...(v.email.trim() ? { email: v.email.trim() } : {}),
    ...(v.passportNumber ? { passportNumber: v.passportNumber.trim().toUpperCase() } : {}),
    ...(v.reason.trim() ? { reason: v.reason.trim() } : {}),
  };
}

// ---------------------------------------------------------------- identity correction request

export const correctionRequestSchema = z.object({
  fields: z.array(z.enum(IDENTITY_FIELDS)).min(1, M.required),
  correctValue: zRequiredString().max(500, 'validation.server.max_length'),
  reason: zRequiredString().max(500, 'validation.server.max_length'),
});
export type CorrectionRequestValues = z.infer<typeof correctionRequestSchema>;
export const correctionRequestDefaults: CorrectionRequestValues = { fields: [], correctValue: '', reason: '' };
/** A request that starts with some details chosen ("Request passport correction" → the passport number). */
export const correctionRequestValues = (fields: CorrectionRequestValues['fields'] = []): CorrectionRequestValues => ({ ...correctionRequestDefaults, fields: [...fields] });

export function toCorrectionRequest(v: CorrectionRequestValues): Schema<'CustomerCorrectionRequestInput'> {
  return { fields: v.fields, correctValue: v.correctValue.trim(), reason: v.reason.trim() };
}

// ---------------------------------------------------------------- employments

export const employmentAddSchema = (today: string) =>
  z
    .object({
      employerId: z.string(),
      /** Free text when the employer is not in the master: kept as entered ("Employer not in master") for Operations. */
      employerName: z.string().optional(),
      joinedOn: optionalDate().refine(notAfter(today), NOT_FUTURE),
      // An empty input is "no amount" (required), never parsed as a number.
      salary: z.string({ error: M.required }).min(1, M.required).pipe(zMoney()),
      isPrimary: z.boolean(),
    })
    .superRefine((v, ctx) => {
      if (v.employerId || v.employerName?.trim()) return;
      ctx.addIssue({ code: 'custom', path: [v.employerName === undefined ? 'employerId' : 'employerName'], message: M.required });
    });
export type EmploymentAddValues = z.infer<ReturnType<typeof employmentAddSchema>>;

/** The first current employment is primary by default; a second concurrent one is not. */
export const employmentAddDefaults = (hasCurrent: boolean): EmploymentAddValues => ({ employerId: '', joinedOn: '', salary: '', isPrimary: !hasCurrent });

export function toEmploymentInput(v: EmploymentAddValues): Schema<'EmploymentInput'> {
  const employer = v.employerId ? { employerId: v.employerId } : { employerName: (v.employerName ?? '').trim() };
  return { ...employer, salary: v.salary, isPrimary: v.isPrimary, ...(v.joinedOn ? { joinedOn: v.joinedOn } : {}) };
}

export const employmentEndSchema = (today: string, joinedOn: string | null) =>
  z.object({
    leftOn: zDateOnly()
      .refine(notAfter(today), NOT_FUTURE)
      .refine((v) => !joinedOn || v >= joinedOn, 'validation.server.before_joining'),
  });
export type EmploymentEndValues = z.infer<ReturnType<typeof employmentEndSchema>>;
export const toEmploymentEnd = (v: EmploymentEndValues): Schema<'EmploymentEnd'> => ({ leftOn: v.leftOn });

// ---------------------------------------------------------------- documents

/** Document types kept on the customer record and reused by every deal. */
export const CUSTOMER_DOCUMENT_TYPES = ['EID', 'PASSPORT_VISA', 'SALARY_CERT', 'BANK_STMT', 'PAYSLIPS', 'EMPLOYER_CONTRACT'] as const;

/** Expiring documents need an expiry date after today: an expired document is never accepted as evidence. */
export const documentUploadSchema = (today: string, expiring: readonly string[]) =>
  z
    .object({
      documentId: z.string(),
      typeCode: zRequiredString(),
      expiresOn: optionalDate(),
      issuedOn: optionalDate().optional(),
      file: z.instanceof(File, { message: M.required }).nullable(),
    })
    .superRefine((v, ctx) => {
      if (!v.file) ctx.addIssue({ code: 'custom', path: ['file'], message: M.required });
      if (v.issuedOn && v.issuedOn > today) ctx.addIssue({ code: 'custom', path: ['issuedOn'], message: 'validation.server.not_future' });
      if (!expiring.includes(v.typeCode)) return;
      if (!v.expiresOn) ctx.addIssue({ code: 'custom', path: ['expiresOn'], message: M.required });
      else if (v.expiresOn <= today) ctx.addIssue({ code: 'custom', path: ['expiresOn'], message: 'validation.server.expired' });
    });
export type DocumentUploadValues = z.infer<ReturnType<typeof documentUploadSchema>>;

export const toDocumentUploadValues = (doc?: Pick<Schema<'CustomerDocument'>, 'id' | 'typeCode'> | null): DocumentUploadValues => ({ documentId: doc?.id ?? '', typeCode: doc?.typeCode ?? '', expiresOn: '', issuedOn: '', file: null });

export function toCustomerDocumentInput(v: DocumentUploadValues, expiring: readonly string[]): Omit<Schema<'CustomerDocumentInput'>, 'uploadId'> {
  return { typeCode: v.typeCode, ...(v.documentId ? { documentId: v.documentId } : {}), expiresOn: expiring.includes(v.typeCode) && v.expiresOn ? v.expiresOn : null, ...(v.issuedOn ? { issuedOn: v.issuedOn } : {}) };
}

// ---------------------------------------------------------------- bank relationships

export const bankRelationshipSchema = z.object({
  bankId: zRequiredString(),
  cif: z.string().regex(/^(\d{6,12})?$/, 'validation.cif'),
  accountNo: z.string().regex(/^(\d{8,16})?$/, 'validation.accountNo'),
  accountStatus: z.enum(['active', 'dormant', 'blacklisted', 'none']),
  relationship: z.enum(['new', 'existing']),
});
export type BankRelationshipValues = z.infer<typeof bankRelationshipSchema>;

export const toBankRelationshipValues = (r: Schema<'CustomerBankRelationship'> | null): BankRelationshipValues => ({ bankId: r?.bankId ?? '', cif: r?.cif ?? '', accountNo: '', accountStatus: r?.accountStatus ?? 'none', relationship: r?.relationship ?? 'new' });

/** The account number is masked on the record: empty keeps the one on file. */
export function toBankRelationshipInput(v: BankRelationshipValues): { bankId: string; body: Schema<'CustomerBankRelationshipInput'> } {
  return { bankId: v.bankId, body: { cif: v.cif.trim() || null, ...(v.accountNo.trim() ? { accountNo: v.accountNo.trim() } : {}), accountStatus: v.accountStatus, relationship: v.relationship } };
}

export const relationshipEndSchema = (today: string) => z.object({ endedOn: zDateOnly().refine(notAfter(today), NOT_FUTURE) });
export type RelationshipEndValues = z.infer<ReturnType<typeof relationshipEndSchema>>;

// ---------------------------------------------------------------- merge

export const mergeSchema = z.object({ otherId: zRequiredString(), survivorId: zRequiredString(), reason: z.string() });
export type MergeValues = z.infer<typeof mergeSchema>;
export const toMergeInput = (v: MergeValues): Schema<'CustomerMergeInput'> => ({ otherId: v.otherId, survivorId: v.survivorId, ...(v.reason.trim() ? { reason: v.reason.trim() } : {}) });
